import { isPlatformServer } from '@angular/common'
import { HttpClient } from '@angular/common/http'
import { inject, Injectable, InjectionToken, LOCALE_ID, PLATFORM_ID, signal } from '@angular/core'
import { AssetCompressionType } from '@mos/shared/types'
import { BehaviorSubject, catchError, forkJoin, of, retry } from 'rxjs'
import { SupportedAssetDetectionService } from './supported-asset-detection.service'

export interface ServerConfig {
	facebookClientId: string
	appleClientId: string
	displayName: string
	copyrightYear: number
	googleClientId: {
		google?: string
		ios?: string
		web: string
	}
	appConfigs: {
		[locale: string]: AppConfig
	}
	debug: boolean
	// AppsFlyer OneLink base URL (https://<subdomain>.onelink.me/<template id>), absent until AppsFlyer is set up
	oneLinkUrl?: string
	// Store pages of the mobile apps, absent while a game isn't published on that store
	appStoreUrl?: string
	playStoreUrl?: string
	// AppsFlyer SDK of the mobile apps (install attribution), absent where it shouldn't run
	appsFlyer?: AppsFlyerConfig
}

export interface AppsFlyerConfig {
	devKey: string
	// Numeric App Store id of the game's iOS app (without the "id" prefix)
	appleAppId: string
}

// Should be generated and deliver by Nginx directly (so not in dev mode)
export interface EnvConfig {
	environment: string
	version: string
	osVersion?: string
	trackingAuthorized?: boolean
	isTestFlight?: boolean
}

export interface AppConfig {
	apiUrl: string
	assetsUrl: string
	siteUrl: string
	preregister: boolean
}

export const CONFIG_INIT = new InjectionToken('CONFIG_INIT')

@Injectable({ providedIn: 'root' })
export class AppConfigService {
	private apiUrl = ''
	private assetsUrl = ''
	private siteUrl = ''
	private oneLinkUrl: string | null = null
	private appStoreUrl: string | null = null
	private playStoreUrl: string | null = null
	private appsFlyer: AppsFlyerConfig | null = null
	private facebookClientId = ''
	private appleClientId = ''
	private displayName = ''
	private copyrightYear = 0
	private googleClientId = { web: '' }
	private preregister = true /* true by default to limit oversights impact */
	private clientApp: 'web' | 'admin' | 'android' | 'ios' = 'web'
	private platform = inject(PLATFORM_ID)
	private localId = inject(LOCALE_ID)
	private supportedCompressionTypes: AssetCompressionType[] = []
	private debug = false
	init$ = new BehaviorSubject(false)
	private env: EnvConfig = {
		environment: 'development',
		version: '1.0.3',
	}
	private appTrackingAuthorized = false
	// Signal et non simple champ : la locale change en cours de session (sélecteur du menu admin, via
	// NavbarRepository) et des computed() en dépendent (voir TranslatorAccessService.canEditI18nFields).
	// Lue depuis un champ nu, la dépendance n'était pas enregistrée et le computed se figeait sur la
	// première valeur - d'autant plus piégeux que la locale persistée est restaurée de façon asynchrone,
	// donc la première lecture tombe souvent sur la valeur par défaut plutôt que sur celle du joueur.
	private readonly currentLocale = signal(this.localId)

	private readonly supportedAssetDetectionService = inject(SupportedAssetDetectionService, { optional: true })
	private readonly http = inject(HttpClient)

	get locale(): string {
		return this.currentLocale()
	}

	set locale(locale: string) {
		this.currentLocale.set(locale)
	}

	private get configFileUrl(): string {
		return `/assets/app-config/${isPlatformServer(this.platform) ? 'configs/prod.json' : 'config.json'}`
	}

	private get envFile(): string {
		return `/env.json`
	}

	init(clientApp: 'android' | 'ios' | 'web' | 'admin' = 'web') {
		this.clientApp = clientApp

		const supportedAssetDetectionService = this.supportedAssetDetectionService
		if (!supportedAssetDetectionService) return Promise.resolve()

		return new Promise<void>((resolve, reject) => {
			forkJoin([
				supportedAssetDetectionService.detect(),
				// No fallback possible here (apiUrl/assetsUrl are required to boot), so retry
				// transient network failures (e.g. "Load failed" on flaky mobile networks -
				// MOS-WEB-AM) before giving up.
				this.http.get<ServerConfig>(this.configFileUrl).pipe(retry({ count: 2, delay: 500 })),
				// Should be generated and deliver by Nginx directly (so not in dev mode)
				this.http.get<EnvConfig>(this.envFile).pipe(
					catchError(() => {
						return of(this.env)
					}),
				),
			]).subscribe({
				next: ([assetCompressionTypes, serverConfig, envConfig]) => {
					this.supportedCompressionTypes = assetCompressionTypes
					let localeConfig = serverConfig.appConfigs[this.currentLocale()]
					if (!localeConfig) {
						localeConfig = serverConfig.appConfigs['fr']
					}
					this.apiUrl = localeConfig.apiUrl
					this.assetsUrl = localeConfig.assetsUrl
					this.siteUrl = localeConfig.siteUrl
					this.oneLinkUrl = serverConfig.oneLinkUrl ?? null
					this.appStoreUrl = serverConfig.appStoreUrl ?? null
					this.playStoreUrl = serverConfig.playStoreUrl ?? null
					this.appsFlyer = serverConfig.appsFlyer ?? null
					this.facebookClientId = serverConfig.facebookClientId
					this.appleClientId = serverConfig.appleClientId
					this.displayName = serverConfig.displayName
					this.copyrightYear = serverConfig.copyrightYear
					this.googleClientId = serverConfig.googleClientId
					this.preregister = localeConfig.preregister
					this.debug = serverConfig.debug
					this.env = envConfig
					this.init$.next(true)
					resolve()
				},
				error: (error) => {
					reject(error)
				},
			})
		})
	}

	getSupportedCompressionTypes(): AssetCompressionType[] {
		return this.supportedCompressionTypes
	}

	getApiUrl(): string {
		return this.apiUrl
	}

	getAssetsUrl(): string {
		return this.assetsUrl
	}

	getSiteUrl(): string {
		return this.siteUrl
	}

	getOneLinkUrl(): string | null {
		return this.oneLinkUrl
	}

	getAppStoreUrl(): string | null {
		return this.appStoreUrl
	}

	getPlayStoreUrl(): string | null {
		return this.playStoreUrl
	}

	getAppsFlyerConfig(): AppsFlyerConfig | null {
		return this.appsFlyer
	}

	getLocale(): string {
		return this.currentLocale()
	}

	getFacebookClientId(): string {
		return this.facebookClientId
	}

	getAppleClientId(): string {
		return this.appleClientId
	}

	getDisplayName(): string {
		return this.displayName
	}

	getCopyrightYear(): number {
		return this.copyrightYear
	}

	getGoogleClientId(): { web: string; google?: string; ios?: string } {
		return this.googleClientId
	}

	isPreregister(): boolean {
		return this.preregister
	}

	isDebug(): boolean {
		return this.debug
	}

	isAdminApp(): boolean {
		return this.clientApp === 'admin'
	}

	isMobileApp(): boolean {
		return this.clientApp === 'android' || this.clientApp === 'ios'
	}

	getClientApp(): 'web' | 'admin' | 'android' | 'ios' {
		return this.clientApp
	}

	getEnv(): EnvConfig {
		return this.env
	}

	setEnv(env: EnvConfig) {
		this.env = env
	}

	authorizeAppTracking() {
		this.appTrackingAuthorized = true
	}

	isAppTrackingAuthorized() {
		return this.appTrackingAuthorized
	}
}
